import React, { memo, useEffect, useState } from 'react';
import JBButton from '@jbhi-fi/lanyard-ui/components/Button';
import JBTypography from '@jbhi-fi/lanyard-ui/components/Typography';
import JBAlert from '@jbhi-fi/lanyard-ui/components/Alert';
import { styles } from './ReviewsContent.css';
import { initializeBazaarvoice } from '../utils/initializeBazaarvoice';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';

type ReviewsContentProps = {
  productSku: number | string;
  isActive: boolean;
  shouldShowTgaDisclaimer?: boolean;
};

const ReviewsContent = memo(({ productSku, isActive, shouldShowTgaDisclaimer = false }: ReviewsContentProps) => {
  const [showReviews, setShowReviews] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [showError, setShowError] = useState(false);

  const disclaimerText = shouldShowTgaDisclaimer ? window.Jbhifi.pdpReviewsContent?.tgaDisclaimerText : '';

  const loadBazaarVoice = (writeReview: boolean) => {
    // Hide error message (it may be visible due to prior error) as we begin to load Bazaar Voice
    setShowError(false);

    // If Bazaarvoice has already been loaded or tab hasn't been selected, do nothing
    if (window.$BV) {
      if (writeReview) {
        window.$BV.ui('rr', 'submit_review', {
          productId: productSku,
        });
      } else {
        setShowLoading(false);
      }
      return;
    }

    const onBVLoad = () => {
      window.$BV.configure('global', {
        productId: productSku,
        events: {
          bvRender: () => {
            setShowLoading(false);
            if (writeReview) {
              window.$BV.ui('rr', 'submit_review', {
                productId: productSku,
              });
            }
          },
        },
      });
      if (window.Jbhifi.storeLocation === 'NZ') {
        //Calling early because BV render is not being called in NZ, will fix it after hearing from BV
        setShowLoading(false);
      }
    };

    const onBVError = () => {
      setShowLoading(false);
      setShowError(true);
    };

    initializeBazaarvoice(onBVLoad, onBVError);
  };

  const handleRetry = () => loadBazaarVoice(false);

  useEffect(() => {
    if (isActive && !showReviews) {
      setShowReviews(true);
      loadBazaarVoice(false);
    }
  }, [isActive]);

  return showReviews ? (
    <div className={styles.root}>
      {disclaimerText ? (
        <JBAlert type="info" showIcon heading="Disclaimer">
          <div className={styles.disclaimerContent}>{disclaimerText}</div>
        </JBAlert>
      ) : null}

      {showLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : null}
      <div data-bv-show="review_highlights" data-bv-product-id={productSku}></div>
      <div data-bv-show="reviews" data-bv-product-id={productSku}></div>
      {showError ? (
        <JBAlert showIcon type="warning" heading="Oops! We couldn't return reviews at the moment">
          <JBTypography margin="0 0 1rem 0">
            <JBButton type="link" onClick={handleRetry}>
              Please try again
            </JBButton>
          </JBTypography>
        </JBAlert>
      ) : null}
    </div>
  ) : null;
});
ReviewsContent.displayName = 'ReviewsContent';

export default ReviewsContent;
